import { useState, useCallback, useRef, useEffect } from 'react'
import { cn } from '@/lib/cn'

interface PiiFieldProps {
  value: string | null | undefined
  presenting: boolean
  className?: string
  revealDuration?: number
}

export function PiiField({ value, presenting, className, revealDuration = 10_000 }: PiiFieldProps) {
  const [revealed, setRevealed] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reveal = useCallback(() => {
    if (!presenting) return
    setRevealed(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setRevealed(false), revealDuration)
  }, [presenting, revealDuration])

  // Re-blur on navigation or when presenting mode toggles
  useEffect(() => {
    setRevealed(false)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [presenting])

  if (!value) return <span className={cn('text-[var(--content-tertiary)]', className)}>--</span>

  const shouldBlur = presenting && !revealed

  return (
    <span
      className={cn(
        'transition-all duration-200 select-none',
        shouldBlur && 'blur-sm cursor-pointer hover:blur-[3px]',
        className,
      )}
      onClick={shouldBlur ? reveal : undefined}
      title={shouldBlur ? 'Click to reveal' : undefined}
    >
      {value}
    </span>
  )
}
